import { useMemo, useState, type ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Divider,
  Input,
  List,
  Space,
  Tag,
  Typography,
} from 'antd'
import { CheckCircleOutlined, SearchOutlined } from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import { formatCST } from '@/lib/time'
import { useIsMobile } from '@/lib/hooks'

export const Route = createFileRoute('/_authed/payment-reconciliation')({
  component: PaymentReconciliationPage,
})

type ReconciliationStatus =
  | 'can_apply'
  | 'already_processed'
  | 'blocked'
  | 'applied'
  | 'failed'

interface CheckResult {
  key: string
  ok: boolean
  message: string
}

interface HiPaySnapshot {
  code: number
  msg?: string
  trade_no?: string
  out_trade_no: string
  api_trade_no?: string
  type?: string
  pid?: number
  addtime?: string
  endtime?: string
  name?: string
  money: string
  status: number
  param?: string
  buyer?: string
}

interface LegacyPaymentSnapshot {
  id: number
  user_id: number
  pay_type: string
  trade_no: string
  pay_price: number | string
  pay_time: number
  create_time: number
  status: number
  month: number
}

interface LegacyUserSnapshot {
  user_id: number
  user_name: string
  user_nick_name: string
  group_id: string
  user_status: number
  user_portrait: string
  user_portrait_thumb: string
  user_points: number
  user_points_froze: number
  user_reg_time: number
  user_reg_ip: number
  user_login_time: number
  user_login_ip: number
  user_last_login_time: number
  user_last_login_ip: number
  user_login_num: number
  user_end_time: number
}

interface LegacySnapshot {
  ok: boolean
  payment?: LegacyPaymentSnapshot
  user?: LegacyUserSnapshot
  error?: string
}

interface NewSiteSnapshot {
  order_id?: string | null
  order_status?: string | null
  trade_no?: string | null
  legacy_id?: number | null
  hypay_trade_no?: string | null
  paid_at?: string | null
  event_status?: string | null
  event_trade_no?: string | null
  legacy_pay_id?: number | null
  event_result?: unknown
  product_snapshot?: unknown
}

interface PreviewResponse {
  ok: true
  action: 'preview'
  status: Extract<
    ReconciliationStatus,
    'can_apply' | 'already_processed' | 'blocked'
  >
  checks: CheckResult[]
  hipay: HiPaySnapshot
  legacy: LegacySnapshot
  new_site: NewSiteSnapshot
  confirm_token: string | null
}

interface ApplyResponse {
  ok: true
  action: 'apply'
  status: Extract<ReconciliationStatus, 'applied' | 'already_processed'>
  result: Record<string, unknown>
}

interface FailureResponse {
  ok: false
  error: string
  status?: ReconciliationStatus
  checks?: CheckResult[]
}

interface PreviewSession {
  outTradeNo: string
  response: PreviewResponse
}

const STATUS_META: Record<
  ReconciliationStatus,
  {
    label: string
    color: string
    alertType: 'success' | 'info' | 'warning' | 'error'
    message: string
  }
> = {
  can_apply: {
    label: '可执行',
    color: 'success',
    alertType: 'success',
    message: '校验通过，可执行补单。',
  },
  already_processed: {
    label: '已处理',
    color: 'warning',
    alertType: 'warning',
    message: '新站已存在订单或事件记录，请先核对再重复操作。',
  },
  blocked: {
    label: '阻断',
    color: 'error',
    alertType: 'error',
    message: '存在阻断项，当前禁止执行补单。',
  },
  applied: {
    label: '已执行',
    color: 'success',
    alertType: 'success',
    message: '补单已提交到新站处理链路。',
  },
  failed: {
    label: '失败',
    color: 'error',
    alertType: 'error',
    message: '补单请求失败，请查看错误信息。',
  },
}

const CHECK_LABELS: Record<string, string> = {
  new_site_processed: '新站重复处理',
  hipay_paid: 'HiPay 支付状态',
  hipay_out_trade_no: 'HiPay 商户单号',
  legacy_payment: '旧站支付记录',
  legacy_trade_no: '旧站订单号',
  amount_match: '金额一致性',
  vip_group: '会员组有效性',
}

const CHECK_MESSAGE_LABELS: Record<string, string> = {
  ok: '通过',
  already_processed: '新站已处理',
  hipay_not_paid: 'HiPay 未支付',
  hipay_out_trade_no_mismatch: 'HiPay 商户单号不一致',
  legacy_payment_missing: '旧站未找到支付记录',
  legacy_trade_no_mismatch: '旧站订单号不一致',
  amount_mismatch: 'HiPay 与旧站金额不一致',
  invalid_vip_group: '旧站会员组不在允许范围',
  payment_not_found: '旧站支付记录不存在',
  user_not_found: '旧站用户不存在',
}

const ERROR_LABELS: Record<string, string> = {
  invalid_json: '请求体不是有效 JSON',
  invalid_request: '请求体格式错误',
  invalid_action: '操作类型不合法',
  missing_out_trade_no: '请输入商户订单号',
  invalid_confirm_token: '确认令牌格式错误',
  missing_confirm_token: '缺少确认令牌，请重新预检',
  stale_confirm_token: '确认令牌已过期或上下文已变化，请重新预检',
  not_applicable: '当前结果不可执行补单',
  unauthorized: '登录态失效，请重新登录',
  forbidden: '当前账号没有权限执行该操作',
  disabled: '补单功能当前未启用',
  server_misconfigured: '服务端配置不完整',
  internal_error: '服务端内部错误',
  apply_failed: '补单执行失败',
}

function PaymentReconciliationPage() {
  const { message } = AntdApp.useApp()
  const [outTradeNo, setOutTradeNo] = useState('')
  const [previewState, setPreviewState] = useState<PreviewSession | null>(null)
  const [requestError, setRequestError] = useState<string | null>(null)
  const [applyResult, setApplyResult] = useState<ApplyResponse | null>(null)
  const isMobile = useIsMobile()
  // 手机上快照改单列，避免 2 列 bordered 把 label 挤成竖排
  const snapshotColumns = isMobile ? 1 : 2

  const trimmedOutTradeNo = outTradeNo.trim()
  const preview =
    previewState?.outTradeNo === trimmedOutTradeNo
      ? previewState.response
      : null

  const canApply =
    preview?.status === 'can_apply' && Boolean(preview?.confirm_token)

  const previewMutation = useMutation({
    mutationFn: invokePreview,
    onSuccess: (response, tradeNo) => {
      setPreviewState({ outTradeNo: tradeNo, response })
      setApplyResult(null)
      setRequestError(null)
      const meta = STATUS_META[response.status]
      if (response.status === 'can_apply') {
        message.success(meta.message)
      } else {
        message.warning(meta.message)
      }
    },
    onError: (error: Error) => {
      setPreviewState(null)
      setApplyResult(null)
      setRequestError(error.message || '预检失败')
      message.error(`预检失败：${error.message || 'unknown'}`)
    },
  })

  const applyMutation = useMutation({
    mutationFn: invokeApply,
    onSuccess: (response) => {
      setApplyResult(response)
      setRequestError(null)
      setPreviewState((current) =>
        current && current.outTradeNo === trimmedOutTradeNo
          ? {
              ...current,
              response: {
                ...current.response,
                status:
                  response.status === 'already_processed'
                    ? 'already_processed'
                    : 'blocked',
                confirm_token: null,
              },
            }
          : current,
      )
      message.success(STATUS_META[response.status].message)
    },
    onError: (error: Error) => {
      setApplyResult(null)
      setRequestError(error.message || '执行失败')
      message.error(`执行失败：${error.message || 'unknown'}`)
    },
  })

  const statusBlock = useMemo(() => {
    const currentStatus = applyResult?.status ?? preview?.status
    if (!currentStatus) return null
    const meta = STATUS_META[currentStatus]
    return {
      color: meta.color,
      label: meta.label,
      alertType: meta.alertType,
      message: meta.message,
    }
  }, [applyResult?.status, preview?.status])

  const currentError = requestError
  const applySummary = applyResult?.result ?? null

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card size="small" title="历史支付补单查验">
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap size={8} className="payrec-query">
            <Input
              value={outTradeNo}
              onChange={(event) => {
                setOutTradeNo(event.target.value)
                setApplyResult(null)
                setRequestError(null)
              }}
              onPressEnter={() => {
                if (trimmedOutTradeNo) {
                  previewMutation.mutate(trimmedOutTradeNo)
                }
              }}
              placeholder="输入商户订单号"
              allowClear
              style={{ width: 320, maxWidth: '100%' }}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={previewMutation.isPending}
              disabled={!trimmedOutTradeNo}
              onClick={() => previewMutation.mutate(trimmedOutTradeNo)}
            >
              预检
            </Button>
            <Button
              type="primary"
              danger
              icon={<CheckCircleOutlined />}
              loading={applyMutation.isPending}
              disabled={!canApply}
              onClick={() => {
                if (!preview?.confirm_token) return
                applyMutation.mutate({
                  outTradeNo: trimmedOutTradeNo,
                  confirmToken: preview.confirm_token,
                })
              }}
            >
              执行补单
            </Button>
          </Space>

          <Space wrap size={[8, 8]} className="payrec-current">
            <Typography.Text type="secondary">当前订单号</Typography.Text>
            <CodeValue value={trimmedOutTradeNo} copyable />
            <Tag color={statusBlock?.color ?? 'default'}>
              {statusBlock?.label ?? '未预检'}
            </Tag>
            <Tag color={preview?.confirm_token ? 'processing' : 'default'}>
              {preview?.confirm_token ? '确认令牌已签发' : '未签发确认令牌'}
            </Tag>
          </Space>

          <Alert
            type="warning"
            showIcon
            message="执行补单会写入新站迁移事件，请先完成预检并确认状态为可执行。"
          />

          {statusBlock ? (
            <Alert
              type={statusBlock.alertType}
              showIcon
              message={statusBlock.message}
            />
          ) : null}

          {currentError ? (
            <Alert type="error" showIcon message={currentError} />
          ) : null}

          {applySummary ? (
            <Alert
              type="info"
              showIcon
              message="执行结果"
              description={<JsonBlock value={applySummary} />}
            />
          ) : null}
        </Space>
      </Card>

      <Card size="small" title="三方快照">
        {preview ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <SnapshotSection
              title="HiPay"
              description="支付网关回包"
              content={
                <Descriptions
                  size="small"
                  bordered
                  column={snapshotColumns}
                  className="payrec-desc"
                >
                  <Descriptions.Item label="商户单号">
                    <CodeValue value={preview.hipay.out_trade_no} copyable />
                  </Descriptions.Item>
                  <Descriptions.Item label="平台单号">
                    <CodeValue value={preview.hipay.api_trade_no} copyable />
                  </Descriptions.Item>
                  <Descriptions.Item label="状态">
                    <Tag
                      color={preview.hipay.status === 1 ? 'success' : 'default'}
                    >
                      {preview.hipay.status === 1
                        ? '已支付'
                        : String(preview.hipay.status)}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="金额">
                    <Typography.Text
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {preview.hipay.money}
                    </Typography.Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="商品">
                    {displayValue(preview.hipay.name)}
                  </Descriptions.Item>
                  <Descriptions.Item label="支付方式">
                    {displayValue(preview.hipay.type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="商户 PID">
                    {displayValue(preview.hipay.pid)}
                  </Descriptions.Item>
                  <Descriptions.Item label="HiPay 订单号">
                    <CodeValue value={preview.hipay.trade_no} copyable />
                  </Descriptions.Item>
                  <Descriptions.Item label="下单时间">
                    {displayValue(preview.hipay.addtime)}
                  </Descriptions.Item>
                  <Descriptions.Item label="支付时间">
                    {displayValue(preview.hipay.endtime)}
                  </Descriptions.Item>
                  <Descriptions.Item label="买家">
                    {displayValue(preview.hipay.buyer)}
                  </Descriptions.Item>
                  <Descriptions.Item label="接口码 / 信息">
                    {`${preview.hipay.code}${preview.hipay.msg ? ` / ${preview.hipay.msg}` : ''}`}
                  </Descriptions.Item>
                </Descriptions>
              }
            />

            <Divider style={{ margin: 0 }} />

            <SnapshotSection
              title="旧站"
              description="legacy 支付与用户快照"
              content={
                <Descriptions
                  size="small"
                  bordered
                  column={snapshotColumns}
                  className="payrec-desc"
                >
                  <Descriptions.Item label="查询结果">
                    <Tag color={preview.legacy.ok ? 'success' : 'error'}>
                      {preview.legacy.ok ? '命中' : '未命中'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="错误">
                    {displayValue(
                      preview.legacy.error
                        ? mapServerError(preview.legacy.error)
                        : null,
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="支付单 ID">
                    {displayValue(preview.legacy.payment?.id)}
                  </Descriptions.Item>
                  <Descriptions.Item label="订单号">
                    <CodeValue
                      value={preview.legacy.payment?.trade_no}
                      copyable
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="订单金额">
                    {displayValue(preview.legacy.payment?.pay_price)}
                  </Descriptions.Item>
                  <Descriptions.Item label="订单状态">
                    {displayValue(preview.legacy.payment?.status)}
                  </Descriptions.Item>
                  <Descriptions.Item label="支付方式">
                    {displayValue(preview.legacy.payment?.pay_type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="月数">
                    {displayValue(preview.legacy.payment?.month)}
                  </Descriptions.Item>
                  <Descriptions.Item label="支付时间">
                    {formatUnixTimestamp(preview.legacy.payment?.pay_time)}
                  </Descriptions.Item>
                  <Descriptions.Item label="创建时间">
                    {formatUnixTimestamp(preview.legacy.payment?.create_time)}
                  </Descriptions.Item>
                  <Descriptions.Item label="用户 ID">
                    {displayValue(preview.legacy.user?.user_id)}
                  </Descriptions.Item>
                  <Descriptions.Item label="用户名">
                    {displayValue(preview.legacy.user?.user_name)}
                  </Descriptions.Item>
                  <Descriptions.Item label="昵称">
                    {displayValue(preview.legacy.user?.user_nick_name)}
                  </Descriptions.Item>
                  <Descriptions.Item label="会员组">
                    {displayValue(preview.legacy.user?.group_id)}
                  </Descriptions.Item>
                  <Descriptions.Item label="用户状态">
                    {displayValue(preview.legacy.user?.user_status)}
                  </Descriptions.Item>
                  <Descriptions.Item label="会员到期">
                    {formatUnixTimestamp(preview.legacy.user?.user_end_time)}
                  </Descriptions.Item>
                </Descriptions>
              }
            />

            <Divider style={{ margin: 0 }} />

            <SnapshotSection
              title="新站"
              description="orders + migration event 快照"
              content={
                <Descriptions
                  size="small"
                  bordered
                  column={snapshotColumns}
                  className="payrec-desc"
                >
                  <Descriptions.Item label="订单 ID">
                    <CodeValue value={preview.new_site.order_id} copyable />
                  </Descriptions.Item>
                  <Descriptions.Item label="订单状态">
                    {displayValue(preview.new_site.order_status)}
                  </Descriptions.Item>
                  <Descriptions.Item label="新站订单号">
                    <CodeValue value={preview.new_site.trade_no} copyable />
                  </Descriptions.Item>
                  <Descriptions.Item label="HiPay 单号">
                    <CodeValue
                      value={preview.new_site.hypay_trade_no}
                      copyable
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="legacy_id">
                    {displayValue(preview.new_site.legacy_id)}
                  </Descriptions.Item>
                  <Descriptions.Item label="legacy_pay_id">
                    {displayValue(preview.new_site.legacy_pay_id)}
                  </Descriptions.Item>
                  <Descriptions.Item label="支付时间">
                    {formatIsoTimestamp(preview.new_site.paid_at)}
                  </Descriptions.Item>
                  <Descriptions.Item label="事件状态">
                    {displayValue(preview.new_site.event_status)}
                  </Descriptions.Item>
                  <Descriptions.Item label="事件 trade_no">
                    <CodeValue
                      value={preview.new_site.event_trade_no}
                      copyable
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="事件结果" span={snapshotColumns}>
                    <JsonBlock value={preview.new_site.event_result} />
                  </Descriptions.Item>
                  <Descriptions.Item label="商品快照" span={snapshotColumns}>
                    <JsonBlock value={preview.new_site.product_snapshot} />
                  </Descriptions.Item>
                </Descriptions>
              }
            />
          </Space>
        ) : (
          <Alert
            type="info"
            showIcon
            message="先输入商户订单号并执行预检，随后查看三方快照。"
          />
        )}
      </Card>

      <Card size="small" title="校验项">
        {preview ? (
          <List
            size="small"
            bordered
            dataSource={preview.checks}
            renderItem={(check) => (
              <List.Item>
                <Space
                  size={12}
                  align="start"
                  style={{ width: '100%', justifyContent: 'space-between' }}
                >
                  <Space direction="vertical" size={2}>
                    <Typography.Text strong>
                      {CHECK_LABELS[check.key] ?? check.key}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {CHECK_MESSAGE_LABELS[check.message] ??
                        mapServerError(check.message)}
                    </Typography.Text>
                  </Space>
                  <Tag color={check.ok ? 'success' : 'error'}>
                    {check.ok ? '通过' : '阻断'}
                  </Tag>
                </Space>
              </List.Item>
            )}
          />
        ) : (
          <Alert
            type="info"
            showIcon
            message="预检后会在这里显示逐项校验结果。"
          />
        )}
      </Card>
    </Space>
  )
}

function SnapshotSection({
  title,
  description,
  content,
}: {
  title: string
  description: string
  content: ReactNode
}) {
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Space direction="vertical" size={0}>
        <Typography.Text strong>{title}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {description}
        </Typography.Text>
      </Space>
      {content}
    </Space>
  )
}

function CodeValue({
  value,
  copyable = false,
}: {
  value: string | number | null | undefined
  copyable?: boolean
}) {
  if (value === null || value === undefined || value === '') {
    return <span>—</span>
  }
  const text = String(value)
  return (
    <Typography.Text
      copyable={copyable ? { text } : false}
      style={{ fontFamily: 'var(--ant-font-family-code, monospace)' }}
    >
      {text}
    </Typography.Text>
  )
}

function JsonBlock({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span>—</span>
  return (
    <pre
      style={{
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: 12,
        lineHeight: 1.5,
        fontFamily: 'var(--ant-font-family-code, monospace)',
      }}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}

function displayValue(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

function formatUnixTimestamp(value: number | null | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return '—'
  }
  return formatCST(value * 1000)
}

function formatIsoTimestamp(value: string | null | undefined) {
  if (!value) return '—'
  return formatCST(value)
}

async function invokePreview(outTradeNo: string): Promise<PreviewResponse> {
  return invokeReconciliation<PreviewResponse>({
    action: 'preview',
    out_trade_no: outTradeNo,
  })
}

async function invokeApply({
  outTradeNo,
  confirmToken,
}: {
  outTradeNo: string
  confirmToken: string
}): Promise<ApplyResponse> {
  return invokeReconciliation<ApplyResponse>({
    action: 'apply',
    out_trade_no: outTradeNo,
    confirm_token: confirmToken,
  })
}

async function invokeReconciliation<
  TSuccess extends PreviewResponse | ApplyResponse,
>(body: {
  action: 'preview' | 'apply'
  out_trade_no: string
  confirm_token?: string
}): Promise<TSuccess> {
  const { data, error } = await supabase.functions.invoke(
    'admin-reconcile-legacy-payment',
    { body },
  )

  if (error) {
    throw new Error(await readInvokeError(error))
  }

  if (!isRecord(data)) {
    throw new Error('响应格式异常')
  }

  if (data.ok !== true) {
    throw new Error(
      typeof data.error === 'string' ? mapServerError(data.error) : '请求失败',
    )
  }

  return data as TSuccess
}

async function readInvokeError(error: unknown): Promise<string> {
  const maybeResponse = (error as { context?: unknown }).context
  if (maybeResponse instanceof Response) {
    const text = await maybeResponse.text()
    if (text) {
      try {
        const parsed = JSON.parse(text) as FailureResponse
        if (parsed && typeof parsed.error === 'string') {
          return mapServerError(parsed.error)
        }
      } catch {
        return text
      }
    }
  }
  if (error instanceof Error) return mapServerError(error.message)
  return '请求失败'
}

function mapServerError(code: string) {
  return ERROR_LABELS[code] ?? CHECK_MESSAGE_LABELS[code] ?? code
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
